import { useState } from "react"
import type { ChangeEvent } from "react"
import { Link, useParams } from "react-router-dom"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import StatusBadge from "@/components/common/StatusBadge"
import { useAdminEntryDetail } from "@/domain/admin/hooks/AdminEntryDetailHook"
import { ENTRY_PLAYER_STATUS_OPTIONS } from "@/domain/entry/model/Entry"
import type { WeightClassOption } from "@/domain/admin/model/AdminEntry"

/**
 * 관리자 신청 상세 화면.
 *
 * 운영자가 바꾸는 것은 **선수 단위**다 — 체급 확정과 선수별 승인 · 반려.
 * 신청 상태(접수 · 취소)는 체육관이 정하므로 여기서는 읽기만 한다.
 */
export default function AdminEntryDetailPage() {
  const { id = "" } = useParams()
  const {
    detail,
    weightClassChoice,
    isLoaded,
    isSaving,
    errorMessage,
    openWeightClassChoice,
    closeWeightClassChoice,
    classifyWeightClass,
    updatePlayerStatus,
  } = useAdminEntryDetail(id)

  const [rejectTargetId, setRejectTargetId] = useState("")
  const [rejectReason, setRejectReason] = useState("")

  function onChangeRejectReason(event: ChangeEvent<HTMLTextAreaElement>) {
    setRejectReason(event.target.value)
  }


  function onClickOpenReject(playerId: string) {
    setRejectTargetId(playerId)
    setRejectReason("")
  }

  function onClickCloseReject() {
    setRejectTargetId("")
    setRejectReason("")
  }

  function onClickConfirmReject() {
    void updatePlayerStatus(
      rejectTargetId,
      "ENTRY_PLAYER_REJECTED",
      rejectReason,
    ).then(() => {
      setRejectTargetId("")
      setRejectReason("")
    })
  }

  function onChangePlayerStatus(
    playerId: string,
    event: ChangeEvent<HTMLSelectElement>,
  ) {
    const status = event.target.value

    if (status === "ENTRY_PLAYER_REJECTED") {
      onClickOpenReject(playerId)
      return
    }

    void updatePlayerStatus(playerId, status)
  }

  function onClickClassify(option: WeightClassOption) {
    void classifyWeightClass(option)
  }

  if (!isLoaded) return <Loading label="신청 내역을 불러오는 중" />

  if (!detail) {
    return (
      <div className="app-page-admin max-w-[960px]">
        <EmptyMessage
          message={errorMessage || "신청 내역을 찾을 수 없습니다."}
        />
        <Link
          to="/admin/entries"
          className="focus-ring app-btn app-btn-primary mx-auto flex max-w-xs"
        >
          목록으로
        </Link>
      </div>
    )
  }

  return (
    <div className="app-page-admin max-w-[1100px]">
      <section className="app-page-head flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="app-txt-eyebrow text-app-primary-600">ENTRY DETAIL</p>
          <h2 className="app-txt-h1 mt-2">{detail.gymName}</h2>
          <p className="app-txt-body2 mt-2 text-app-text-200">
            <span className="font-mono">{detail.entryNo}</span> ·{" "}
            {detail.tournamentName} · 담당자 {detail.gymContactName} (
            {detail.gymContactPhoneNo})
          </p>
        </div>

        <div className="flex items-end gap-3">
          {/* 신청 상태는 체육관이 정한다 — 운영자가 바꾸는 것은 아래 표의 선수 상태다 */}
          <div className="flex flex-col gap-1.5">
            <span className="app-label-sub">신청 상태 (체육관 접수 건)</span>
            <span className="flex items-center gap-2">
              <StatusBadge label={detail.statusLabel} />
              {detail.canceledReason && (
                <span className="app-txt-caption text-app-red-300">
                  {detail.canceledReason}
                </span>
              )}
            </span>
          </div>

          <Link
            to="/admin/entries"
            className="focus-ring app-btn app-btn-outline"
          >
            목록으로
          </Link>
        </div>
      </section>

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mb-5"
        >
          {errorMessage}
        </p>
      )}

      <div className="app-surface overflow-x-auto">
        <table className="app-table min-w-[900px]">
          <caption className="sr-only">신청 선수 목록</caption>
          <thead>
            <tr>
              <th scope="col">선수</th>
              <th scope="col">종목 · 부</th>
              <th scope="col">체중 · 경력</th>
              <th scope="col">체급</th>
              <th scope="col">선수 상태 (승인 · 반려)</th>
            </tr>
          </thead>
          <tbody>
            {detail.players.map((player) => (
              <tr key={player.id}>
                <td>
                  <span className="block font-bold">{player.name}</span>
                  <span className="app-txt-caption block text-app-text-200">
                    {player.birthDate} · {player.genderLabel}
                  </span>
                </td>
                <td className="text-app-text-300">
                  {player.sportLabel}
                  <span className="app-txt-caption block text-app-text-200">
                    {player.divisionLabel}
                  </span>
                </td>
                <td className="text-app-text-300">
                  {/* 닭싸움은 기록을 받지 않아 칸이 빈다 */}
                  {player.weightText || "해당 없음"}
                  <span className="app-txt-caption block text-app-text-200">
                    {player.careerText}
                  </span>
                </td>
                <td>
                  <span className="block">{player.weightClassName}</span>
                  <button
                    type="button"
                    onClick={() => openWeightClassChoice(player.id)}
                    disabled={isSaving}
                    className="focus-ring app-btn app-btn-sm app-btn-brand mt-1.5"
                  >
                    체급 확정
                  </button>
                </td>
                <td>
                  <select
                    value=""
                    onChange={(event) => onChangePlayerStatus(player.id, event)}
                    disabled={isSaving}
                    aria-label={`${player.name} 선수 상태 변경`}
                    className="focus-ring app-field app-field-sm w-auto"
                  >
                    <option value="">{player.statusLabel} (변경)</option>
                    {ENTRY_PLAYER_STATUS_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {player.rejectedReason && (
                    <span className="app-txt-caption mt-1 block text-app-red-300">
                      {player.rejectedReason}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {weightClassChoice && (
        <div className="app-dialog-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="classify-title"
            className="app-dialog max-h-[80dvh] max-w-lg overflow-y-auto"
          >
            <h3
              id="classify-title"
              className="app-txt-h3"
            >
              체급 확정
            </h3>
            <p className="app-txt-body2 mt-2 text-app-text-200">
              체중 · 부 · 성별로 좁힌 후보입니다. 후보가 없으면 종목 전체에서
              고르세요.
            </p>

            {weightClassChoice.candidates.length > 0 && (
              <ul className="mt-4 flex list-none flex-col gap-2">
                {weightClassChoice.candidates.map((option) => (
                  <li key={option.key}>
                    <button
                      type="button"
                      onClick={() => onClickClassify(option)}
                      className="focus-ring app-btn app-btn-brand app-btn-block justify-start"
                    >
                      {option.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <details className="mt-5">
              <summary className="app-txt-body2 cursor-pointer text-app-text-300">
                종목 전체 체급에서 고르기 ({weightClassChoice.allOfSport.length}
                개)
              </summary>
              <ul className="mt-3 flex list-none flex-col gap-1">
                {weightClassChoice.allOfSport.map((option) => (
                  <li key={option.key}>
                    <button
                      type="button"
                      onClick={() => onClickClassify(option)}
                      className="focus-ring app-btn app-btn-sm app-btn-quiet app-btn-block justify-start"
                    >
                      {option.name}
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <button
              type="button"
              onClick={closeWeightClassChoice}
              className="focus-ring app-btn app-btn-outline app-btn-block mt-6"
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {rejectTargetId && (
        <div className="app-dialog-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-title"
            className="app-dialog"
          >
            <h3
              id="reject-title"
              className="app-txt-h3"
            >
              반려 사유
            </h3>
            <p className="app-txt-body2 mt-2 text-app-text-200">
              사유는 체육관 조회 화면에 그대로 보입니다.
            </p>

            <textarea
              value={rejectReason}
              rows={3}
              maxLength={200}
              onChange={onChangeRejectReason}
              aria-label="반려 사유"
              className="focus-ring app-field mt-3"
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClickCloseReject}
                className="focus-ring app-btn app-btn-outline"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={onClickConfirmReject}
                disabled={isSaving || !rejectReason.trim()}
                className="focus-ring app-btn app-btn-danger"
              >
                반려 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
